import React, { useState, useEffect } from 'react';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../component/b2c/navBar/navBar';
import { Phone, MapPin, Mail, Plus, X, User, Briefcase, Calendar, Check, Eye, Pencil, Trash2, BadgeCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const employees = [
    {
        id: 'EMP001',
        name: 'Rahul Sharma',
        email: 'rahul@example.com',
        phone: '+91 98765 43210',
        address: 'Sector 18, Noida, UP',
        designation: 'Frontend Dev',
        joining: '12 Jan 2023',
        img: 'https://i.pravatar.cc/150?img=1'
    },
    {
        id: 'EMP002',
        name: 'Aman Verma',
        email: 'aman@example.com',
        phone: '+91 91234 56780',
        address: 'Andheri West, Mumbai, MH',
        designation: 'Backend Dev',
        joining: '05 Mar 2022',
        img: 'https://i.pravatar.cc/150?img=2'
    },
    {
        id: 'EMP003',
        name: 'Shivani Sharma',
        email: 'shivani@example.com',
        phone: '+91 99887 66554',
        address: 'Connaught Place, Delhi',
        designation: 'HR Manager',
        joining: '20 Jul 2021',
        img: 'https://i.pravatar.cc/150?img=3'
    },
    {
        id: 'EMP004',
        name: 'Rohit Kumar',
        email: 'rohit@example.com',
        phone: '+91 90909 80807',
        address: 'Whitefield, Bengaluru, KA',
        designation: 'UI Designer',
        joining: '10 Feb 2024',
        img: 'https://i.pravatar.cc/150?img=4'
    },
    {
        id: 'EMP005',
        name: 'Simran Kaur',
        email: 'simran@example.com',
        phone: '+91 93456 12378',
        address: 'Model Town, Ludhiana, PB',
        designation: 'QA Tester',
        joining: '18 Aug 2023',
        img: 'https://i.pravatar.cc/150?img=5'
    },
    {
        id: 'EMP006',
        name: 'Vikas Gupta',
        email: 'vikas@example.com',
        phone: '+91 97123 45690',
        address: 'Banjara Hills, Hyderabad, TS',
        designation: 'DevOps',
        joining: '01 Jan 2022',
        img: 'https://i.pravatar.cc/150?img=6'
    }
];

// 👉 Designation color map (purely visual, easy to extend)
const designationStyles = {
    'Frontend Dev': 'from-blue-500 to-cyan-400',
    'Backend Dev': 'from-violet-500 to-purple-400',
    'HR Manager': 'from-pink-500 to-rose-400',
    'UI Designer': 'from-amber-500 to-orange-400',
    'QA Tester': 'from-emerald-500 to-teal-400',
    'DevOps': 'from-slate-600 to-slate-400',
};

const initialForm = {
    name: '',
    email: '',
    phone: '',
    address: '',
    designation: '',
    joining: '',
};

const designationOptions = ['Frontend Dev', 'Backend Dev', 'HR Manager', 'UI Designer', 'QA Tester', 'DevOps'];

const Hrms = () => {
    const [employeeList, setEmployeeList] = useState(employees);
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);

    const employeesPerPage = 8;

    const totalPages = Math.ceil(employeeList.length / employeesPerPage);
    const indexOfLast = currentPage * employeesPerPage;
    const indexOfFirst = indexOfLast - employeesPerPage;
    const currentEmployees = employeeList.slice(indexOfFirst, indexOfLast);

    // 👉 LOCK BODY SCROLL WHEN MODAL OPEN
    useEffect(() => {
        document.body.style.overflow = isModalOpen ? 'hidden' : 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [isModalOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!form.name.trim()) newErrors.name = 'Name is required';
        if (!form.email.trim()) newErrors.email = 'Email is required';
        else if (!/^\S+@\S+\.\S+$/.test(form.email)) newErrors.email = 'Enter a valid email';
        if (!form.phone.trim()) newErrors.phone = 'Phone number is required';
        if (!form.address.trim()) newErrors.address = 'Address is required';
        if (!form.designation.trim()) newErrors.designation = 'Select a designation';
        if (!form.joining.trim()) newErrors.joining = 'Joining date is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setForm(initialForm);
        setErrors({});
    };

    const handleAddEmployee = () => {
        if (!validateForm()) return;

        const newEmployee = {
            id: `EMP${String(employeeList.length + 1).padStart(3, '0')}`,
            name: form.name,
            email: form.email,
            phone: form.phone,
            address: form.address,
            designation: form.designation,
            joining: formatDate(form.joining),
            img: `https://i.pravatar.cc/150?img=${(employeeList.length % 70) + 1}`,
        };

        setEmployeeList(prev => [...prev, newEmployee]);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2200);
        closeModal();
        setCurrentPage(Math.ceil((employeeList.length + 1) / employeesPerPage));
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-gray-100">

            <NavBar />

            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <div className="flex flex-col bg-gradient-to-br from-blue-100 to-gray-200 w-full p-4 md:p-6 overflow-hidden">

                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5">
                        <div>
                            <h2 className="text-xl md:text-2xl font-bold text-gray-800">Employee Details</h2>
                            <p className="text-sm text-gray-500">{employeeList.length} total employees</p>
                        </div>
                        <Link to='/hrms/add-employee'>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.03] active:scale-[0.98] transition-all duration-200"
                            >
                                <Plus size={18} />
                                Add Emp
                            </button>
                        </Link>
                    </div>

                    {/* Card grid container */}
                    <div className="flex-1 overflow-y-auto no-scrollbar pb-2">

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                            {currentEmployees.map((emp, i) => {
                                const gradient = designationStyles[emp.designation] || 'from-gray-500 to-gray-400';
                                return (
                                    <div key={emp.id} className="group h-[240px] [perspective:1200px]">
                                        <div className="relative w-full h-full transition-transform duration-700 ease-out [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">

                                            {/* ====== FRONT FACE — ID CARD LOOK ====== */}
                                            <div className="absolute inset-0 [backface-visibility:hidden] rounded-2xl overflow-hidden shadow-lg border border-white/60 bg-white flex flex-col">
                                                {/* top banner */}
                                                <div className={`relative h-20 bg-gradient-to-r ${gradient}`}>
                                                    <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/15 rounded-full"></div>
                                                    <div className="absolute -bottom-8 -left-4 w-20 h-20 bg-white/10 rounded-full"></div>
                                                    <span className="absolute top-3 right-3 text-[10px] font-mono text-white/85 bg-black/15 px-2 py-0.5 rounded-full">
                                                        {emp.id}
                                                    </span>
                                                </div>

                                                {/* avatar overlapping banner */}
                                                <div className="flex flex-col items-center -mt-10 px-4">
                                                    <div className="relative">
                                                        <img
                                                            src={emp.img}
                                                            className="w-20 h-20 rounded-full ring-4 ring-white shadow-lg object-cover"
                                                        />
                                                        <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full ring-2 ring-white flex items-center justify-center">
                                                            <BadgeCheck size={11} className="text-white" />
                                                        </span>
                                                    </div>

                                                    <h3 className="mt-3 font-semibold text-gray-800 text-sm text-center leading-tight">
                                                        {emp.name}
                                                    </h3>
                                                    <span className={`mt-1.5 inline-block px-3 py-1 text-[11px] font-medium rounded-full bg-gradient-to-r ${gradient} text-white shadow-sm`}>
                                                        {emp.designation}
                                                    </span>
                                                </div>

                                                <div className="mt-auto px-4 py-3 border-t border-gray-100 flex items-center justify-center gap-1.5 text-[11px] text-gray-400">
                                                </div>
                                            </div>

                                            {/* ====== BACK FACE — FULL DETAILS ====== */}
                                            <div className={`absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-2xl overflow-hidden shadow-lg border border-white/60 bg-gradient-to-br ${gradient} text-white flex flex-col p-4`}>
                                                <div className="flex items-center gap-3 pb-3 border-b border-white/25">
                                                    <img
                                                        src={emp.img}
                                                        className="w-11 h-11 rounded-full ring-2 ring-white/70 object-cover shrink-0"
                                                    />
                                                    <div className="min-w-0">
                                                        <p className="font-semibold text-sm truncate">{emp.name}</p>
                                                        <p className="text-[11px] text-white/80 font-mono">{emp.id}</p>
                                                    </div>
                                                </div>

                                                <div className="flex-1 py-3 space-y-2 text-[12px] overflow-hidden">
                                                    <div className="flex items-start gap-2">
                                                        <Mail size={13} className="mt-0.5 text-white/80 shrink-0" />
                                                        <span className="break-all leading-tight">{emp.email}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Phone size={13} className="text-white/80 shrink-0" />
                                                        <span>{emp.phone}</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <MapPin size={13} className="mt-0.5 text-white/80 shrink-0" />
                                                        <span className="leading-tight">{emp.address}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Calendar size={13} className="text-white/80 shrink-0" />
                                                        <span>Joined {emp.joining}</span>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between gap-2 pt-3 border-t border-white/25">
                                                    <button className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors text-[11px]">
                                                        <Eye size={13} /> View
                                                    </button>
                                                    <button className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors text-[11px]">
                                                        <Pencil size={13} /> Edit
                                                    </button>
                                                    <button className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-white/15 hover:bg-red-500/80 transition-colors text-[11px]">
                                                        <Trash2 size={13} /> Del
                                                    </button>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>

                    {/* Pagination */}
                    <div className="flex justify-center items-center gap-2 mt-5 flex-wrap shrink-0">

                        <button
                            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors text-sm"
                        >
                            Prev
                        </button>

                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${currentPage === i + 1
                                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/30'
                                        : 'border border-gray-300 bg-white hover:bg-gray-50'
                                    }`}
                            >
                                {i + 1}
                            </button>
                        ))}

                        <button
                            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors text-sm"
                        >
                            Next
                        </button>

                    </div>

                </div>
            </div>

            {/* ============ ADD EMPLOYEE MODAL ============ */}
            {/* {isModalOpen && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn"
                    onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}
                >
                    <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-scaleIn max-h-[90vh] flex flex-col">

                    
                        <div className="relative px-6 py-5 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white shrink-0">
                            <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full"></div>
                            <div className="absolute -bottom-12 -left-6 w-28 h-28 bg-white/10 rounded-full"></div>
                            <div className="relative flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-bold">Add New Employee</h3>
                                    <p className="text-xs text-white/80 mt-0.5">Fill in the details to onboard a new team member</p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="p-2 rounded-full hover:bg-white/20 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                    
                        <div className="p-6 space-y-4 overflow-y-auto no-scrollbar">

                            
                            <div>
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Full Name</label>
                                <div className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border-2 transition-colors ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200 focus-within:border-blue-500 bg-gray-50'}`}>
                                    <User size={16} className="text-gray-400 shrink-0" />
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="Full Name"
                                        className="w-full bg-transparent outline-none text-sm text-gray-800 placeholder:text-gray-300"
                                    />
                                </div>
                                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                            </div>

                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Email</label>
                                    <div className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border-2 transition-colors ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 focus-within:border-blue-500 bg-gray-50'}`}>
                                        <Mail size={16} className="text-gray-400 shrink-0" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            placeholder="youremail@example.com"
                                            className="w-full bg-transparent outline-none text-sm text-gray-800 placeholder:text-gray-300"
                                        />
                                    </div>
                                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                                </div>

                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Phone Number</label>
                                    <div className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border-2 transition-colors ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200 focus-within:border-blue-500 bg-gray-50'}`}>
                                        <Phone size={16} className="text-gray-400 shrink-0" />
                                        <input
                                            type="text"
                                            name="phone"
                                            value={form.phone}
                                            onChange={handleChange}
                                            placeholder="Mobile No."
                                            className="w-full bg-transparent outline-none text-sm text-gray-800 placeholder:text-gray-300"
                                        />
                                    </div>
                                    {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Address</label>
                                <div className={`flex items-start gap-2 px-3.5 py-2.5 rounded-xl border-2 transition-colors ${errors.address ? 'border-red-300 bg-red-50' : 'border-gray-200 focus-within:border-blue-500 bg-gray-50'}`}>
                                    <MapPin size={16} className="text-gray-400 shrink-0 mt-0.5" />
                                    <input
                                        type="text"
                                        name="address"
                                        value={form.address}
                                        onChange={handleChange}
                                        placeholder="e.g. Sector 62, Noida, UP"
                                        className="w-full bg-transparent outline-none text-sm text-gray-800 placeholder:text-gray-400"
                                    />
                                </div>
                                {errors.address && <p className="text-xs text-red-500 mt-1">{errors.address}</p>}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Designation</label>
                                    <div className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border-2 transition-colors ${errors.designation ? 'border-red-300 bg-red-50' : 'border-gray-200 focus-within:border-blue-500 bg-gray-50'}`}>
                                        <Briefcase size={16} className="text-gray-400 shrink-0" />
                                        <select
                                            name="designation"
                                            value={form.designation}
                                            onChange={handleChange}
                                            className="w-full bg-transparent outline-none text-sm text-gray-800"
                                        >
                                            <option value="">Select role</option>
                                            {designationOptions.map(opt => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.designation && <p className="text-xs text-red-500 mt-1">{errors.designation}</p>}
                                </div>

                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Joining Date</label>
                                    <div className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border-2 transition-colors ${errors.joining ? 'border-red-300 bg-red-50' : 'border-gray-200 focus-within:border-blue-500 bg-gray-50'}`}>
                                        <Calendar size={16} className="text-gray-400 shrink-0" />
                                        <input
                                            type="date"
                                            name="joining"
                                            value={form.joining}
                                            onChange={handleChange}
                                            className="w-full bg-transparent outline-none text-sm text-gray-800"
                                        />
                                    </div>
                                    {errors.joining && <p className="text-xs text-red-500 mt-1">{errors.joining}</p>}
                                </div>
                            </div>

                        </div>

                        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100 shrink-0">
                            <button
                                onClick={closeModal}
                                className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddEmployee}
                                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                            >
                                <Plus size={16} />
                                Add Employee
                            </button>
                        </div>

                    </div>
                </div>
            )} */}

            {/* ============ SUCCESS TOAST ============ */}
            {/* {showSuccess && (
                <div className="fixed bottom-6 right-6 z-[110] flex items-center gap-3 px-5 py-3.5 bg-white rounded-2xl shadow-2xl border border-emerald-100 animate-slideUp">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                        <Check size={16} className="text-white" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-800">Employee Added!</p>
                        <p className="text-xs text-gray-500">New employee has been onboarded successfully</p>
                    </div>
                </div>
            )} */}

            {/* ============ ANIMATIONS ============ */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scaleIn {
                    from { opacity: 0; transform: scale(0.92) translateY(10px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
                .animate-scaleIn { animation: scaleIn 0.25s ease-out; }
                .animate-slideUp { animation: slideUp 0.3s ease-out; }
            `}</style>
        </div>
    );
};

export default Hrms;